import type { ComponentInstance } from './hook'
import type { ReactiveEffectLike } from './lifecycle'

export interface RequestContext {
    currentComponent: ComponentInstance | null
    activeEffect: ReactiveEffectLike | undefined
    effectQueue: Set<ReactiveEffectLike>
    isFlushPending: boolean
    isBatching: boolean
    componentNameStack: string[]
}

function createDefault(): RequestContext {
    return {
        currentComponent: null,
        activeEffect: undefined,
        effectQueue: new Set(),
        isFlushPending: false,
        isBatching: false,
        componentNameStack: [],
    }
}

// Try to load AsyncLocalStorage (Node only). Use a guarded synchronous probe.
type ALS = {
    run<T>(store: RequestContext, fn: () => T): T
    getStore(): RequestContext | undefined
}

let als: ALS | null = null
const fallbackContext: RequestContext = createDefault()

// Detection: only load node:async_hooks under Node. Bundlers (tsup) tree-shake the
// import path if it's behind a typeof check.
const isNode = typeof globalThis !== 'undefined'
    && typeof (globalThis as { process?: { versions?: { node?: string } } }).process?.versions?.node === 'string'

if (isNode) {
    try {
        // Use Function constructor to avoid static analysis flagging this import in browser builds.
        // The isNode guard ensures this only ever executes in Node environments.
        const requireFn = new Function('return require')() as ((m: string) => unknown) | undefined
        if (requireFn) {
            const ah = requireFn('node:async_hooks') as { AsyncLocalStorage: new () => ALS }
            als = new ah.AsyncLocalStorage()
        }
    } catch {
        // fall through — fallback context used in browser / Edge environments
    }
}

export function getRequestContext(): RequestContext {
    if (als) {
        const store = als.getStore()
        if (store) return store
    }
    return fallbackContext
}

export function runWithRequestContext<T>(fn: () => T): T {
    if (als) {
        return als.run(createDefault(), fn)
    }
    return fn()
}

/** @internal — for tests only */
export function __resetFallback(): void {
    fallbackContext.currentComponent = null
    fallbackContext.activeEffect = undefined
    fallbackContext.effectQueue.clear()
    fallbackContext.isFlushPending = false
    fallbackContext.isBatching = false
    fallbackContext.componentNameStack.length = 0
}
